import { specValue } from '../cars/carFigures';
import { specSheet } from '../cars/specSheet';
import { duelSpec } from '../game/duelSpecs';

/**
 * Two cars, on every figure the catalogue publishes (SPEC-008 PR-8.4).
 *
 * The rows ARE `specSheet`'s rows. This module calls the same function the
 * detail card's table calls and adds a second column and a mark, rather than
 * composing its own list of "comparable" figures — two lists is how a row gets
 * added to one table and not the other, and the reader who notices is the reader
 * who opened both screens to check.
 *
 * Which end of a figure LEADS is `duelSpecs`' direction table wherever that
 * table has an opinion, for the same reason. It already knows that less wins on
 * 0–100, on weight and on production and that more wins on the rest, and a
 * second copy here would be a second place for "lower is better" to be wrong
 * about one figure.
 *
 * What it deliberately does NOT reuse from that table is the minimum margin. A
 * duel refuses to ask a question two cars are a hair apart on, because losing a
 * streak to a coin toss is what makes a reader close the screen. A comparison
 * has no stakes and no streak: the reader asked what the two figures are, and
 * one km/h is a real answer to that. So a lead here is any lead at all, and how
 * small it is stays visible in the two numbers beside it.
 *
 * The one thing dropped from `specSheet`'s rows is the NOTE — the trap speed
 * under the standing quarter. On the detail card that is one line under one
 * number; in two columns on a phone it is two, under the densest table in the
 * product, and the race band above already prints both cars' trap speeds where
 * the reader is watching for them. Everything else is carried by SUBTRACTION so
 * that a field added to a row upstream arrives here without anybody having to
 * remember this file.
 *
 * Pure, and it returns the same shape `specSheet` does — message keys and
 * formatted values, never resolved copy.
 */

/**
 * The figures the duel does not ask about but a comparison still ranks.
 *
 * `duelSpecs` is a list of QUESTIONS, not a list of directions, and the two
 * differ in exactly these two rows. Power-to-weight is derived rather than
 * published and nobody quotes it from memory, which makes it a poor thing to
 * guess at; the standing quarter is missing for fourteen of the twenty-four, which
 * makes it a question the game could rarely ask. Neither objection survives the
 * move to a table the reader is reading rather than guessing at, and both have
 * an unambiguous better end — so leaving them unmarked would have this band
 * silent about the two most decisive rows on it.
 *
 * It is a supplement rather than an extension of `DUEL_SPECS` because adding a
 * question to the duel is SPEC-007's change and would alter a shipped game.
 */
const SUPPLEMENTARY_DIRECTIONS = {
    powerToWeight: 'higher',
    quarterMile: 'lower',
};

/** Which end of this figure is the better one, or null where neither is. */
function winningEnd(key) {
    return duelSpec(key)?.wins ?? SUPPLEMENTARY_DIRECTIONS[key] ?? null;
}

/**
 * Which car leads a row, or null where neither does.
 *
 * Null covers three cases and they are one case: a row that is not a figure at
 * all (the engine, the drivetrain, the production years — facts with no better
 * end), a figure this table has no direction for, and a tie.
 *
 * A TIE MARKS NEITHER CAR, not both (D-16). Two cars that share a top speed have
 * not each won it, and a table with both marks lit reads as two winners rather
 * than as a draw.
 *
 * Money is deliberately outside both direction tables, so a price row never leads.
 * Marking one would be this product deciding that dearer is better, which is a
 * claim about cars it has spent five specs not making.
 */
function leaderIndex(cars, key) {
    const wins = winningEnd(key);

    if (!wins) {
        return null;
    }

    const values = cars.map((car) => specValue(car, key));

    // A figure one car does not carry is led by the one that does. The reader
    // can see the em dash; what they cannot see is whether the absence lost.
    if (values.some((value) => value === null)) {
        return values.every((value) => value === null) ? null : values.findIndex((value) => value !== null);
    }

    if (values[0] === values[1]) {
        return null;
    }

    const leadsOnFirst = wins === 'higher' ? values[0] > values[1] : values[0] < values[1];

    return leadsOnFirst ? 0 : 1;
}

/**
 * The specification table, doubled.
 *
 * One row per figure in `specSheet`'s own order, each carrying the two cars'
 * formatted values and which of them leads. The label, the unit key and the
 * absence dash all come from the first car's row — they are properties of the
 * FIGURE rather than of the car, and taking them from one side keeps a row from
 * being labelled twice in two ways.
 *
 * The exception is the dated money label, which genuinely differs per car: the
 * F40's list price is a 1987 price and the Supra's is a 1993 one, and a row that
 * borrowed one year for both would put a false date under a real number. So
 * `labelValues` is carried per side and the component picks.
 */
export function compareSpecs(cars, { languageCode = 'en' } = {}) {
    if (!Array.isArray(cars) || cars.length !== 2 || cars.some((car) => !car)) {
        return [];
    }

    const sheets = cars.map((car) => specSheet(car, { languageCode }));

    return sheets[0].map((row, index) => ({
        key: row.key,
        labelKey: row.labelKey,
        leader: leaderIndex(cars, row.key),
        // Everything the sheet put on the row EXCEPT its note, carried by
        // subtraction rather than by re-listing. A named list of five fields is
        // a list that silently drops the sixth: this sheet grew a compression
        // ratio and a gear count while this module was being written, both of
        // which carry `valueValues`, and a hand-written pick-list rendered
        // "{gears}-speed manual" at the reader for exactly as long as it took
        // somebody to look.
        sides: sheets.map(({ [index]: { key: rowKey, noteKey, noteValues, ...side } }) => side),
    }));
}

/**
 * How many rows each car leads.
 *
 * The one summary this band draws, and it is a count rather than a verdict: "the
 * 959 leads on seven figures" is arithmetic the reader can check by scrolling,
 * where "the 959 is the better car" is an opinion the catalogue has not earned.
 * Rows nobody leads are in neither total and that is the point — a comparison
 * where four rows are ties is a different comparison from one where none are.
 */
export function compareTally(rows) {
    return rows.reduce(
        (tally, row) => (row.leader === null ? tally : tally.map(
            (count, index) => (index === row.leader ? count + 1 : count),
        )),
        [0, 0],
    );
}
